import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { getCashDurationShortTerm, getCashMovementTerms, getForecastTerm } from '@/lib/terminology-config';

export type TourPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'left' | 'right' | 'top' | 'bottom';

export interface TourStep {
  id: string;
  title: string;
  content: string;
  target?: string; // CSS selector for element to highlight
  position?: TourPosition;
  navigateTo?: string; // Page to navigate to
  action?: () => void; // Optional action to perform
  roles?: string[]; // Role template IDs that should see this step (e.g. 'manager', 'cfo', 'system_admin')
  interactive?: boolean; // If true, wait for user action before enabling Next
  interactiveTarget?: string; // Element to watch for interaction
}

export interface Tour {
  id: string;
  name: string;
  description: string;
  duration: string;
  roles?: string[]; // Role template IDs that should see this tour
  steps: TourStep[];
}

interface TourState {
  userId: string;
  completedTours: string[];
  inProgressTours: Record<string, number>;
  hideWelcome: boolean;
}

interface TourContextType {
  tours: Tour[];
  activeTour: Tour | null;
  currentStepIndex: number;
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
  startTour: (tourId: string) => void;
  nextStep: () => void;
  previousStep: () => void;
  skipTour: () => void;
  completeTour: () => void;
  replayTour: (tourId: string) => void;
  hasSeenWelcomePrompt: boolean;
  setHasSeenWelcomePrompt: (seen: boolean) => void;
  isTourCompleted: (tourId: string) => boolean;
  getTourState: () => TourState;
  currentRoleId: string;
  setCurrentRoleId: (roleId: string) => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

// Define all tours with role-based content
const ALL_TOURS: Tour[] = [
  {
    id: 'getting-started',
    name: 'Getting Started',
    description: 'Learn the basics of Access CFF',
    duration: '10 min',
    steps: [
      {
        id: 'welcome',
        title: 'Welcome to Access CFF',
        content: 'Welcome! This quick tour will help you understand the key features and how to navigate the system.',
        position: 'bottom',
        roles: ['manager', 'cfo', 'regional', 'preparer', 'external', 'executive'],
      },
      {
        id: 'welcome-admin',
        title: 'Welcome, System Administrator',
        content: 'As a System Administrator, you\'re responsible for configuring data sources, managing users, and monitoring system health. This tour covers your key responsibilities.',
        position: 'bottom',
        roles: ['system_admin'],
      },
      {
        id: 'navigation',
        title: 'Navigation Menu',
        content: 'Use the sidebar to access different sections: Dashboard, Transactions, Scenarios, Reports, and Settings.',
        target: '[data-tour="sidebar"]',
        position: 'right',
        roles: ['manager', 'cfo', 'regional', 'preparer', 'external', 'executive'],
      },
      {
        id: 'navigation-admin',
        title: 'Admin Navigation',
        content: 'Your key areas: Settings → Data Sources (configure connections), Settings → Users (manage access), and Settings → System (monitor health).',
        target: '[data-tour="sidebar"]',
        position: 'right',
        roles: ['system_admin'],
      },
      {
        id: 'admin-responsibilities',
        title: 'Key Responsibilities',
        content: 'You\'ll manage: 1) Data source connections and sync schedules, 2) User accounts and role assignments, 3) Company-wide settings like terminology, 4) System monitoring and audit logs.',
        position: 'bottom',
        roles: ['system_admin'],
      },
      {
        id: 'help',
        title: 'Getting Help',
        content: 'Click the ? icon in the header to access tours anytime, or use the 💬 chat assistant for instant answers.',
        position: 'bottom',
      },
    ],
  },
  {
    id: 'dashboard-overview',
    name: 'Dashboard Overview',
    description: 'Understand your dashboard at a glance',
    duration: '5 min',
    steps: [
      {
        id: 'welcome-ceo',
        title: 'Your 10-Second Check',
        content: 'This is your 10-second check. Green line = healthy, amber = attention needed, red = action required.',
        position: 'bottom',
        roles: ['executive'],
      },
      {
        id: 'welcome-manager',
        title: 'Welcome to Dashboard Tour',
        content: 'The dashboard shows your 90-day projected cash with daily granularity. Let\'s explore the key features.',
        position: 'bottom',
        roles: ['manager', 'cfo', 'regional'],
      },
      {
        id: 'welcome-preparer',
        title: `Your ${getForecastTerm(true)} Dashboard`,
        content: `The dashboard shows the ${getForecastTerm(false)} you've prepared from transaction data. Let's review the key sections.`,
        position: 'bottom',
        roles: ['preparer', 'external'],
      },
      {
        id: 'current-cash',
        title: 'Current Cash Position',
        content: 'This shows your total cash across all bank accounts. The number updates in real-time as transactions sync.',
        target: '[data-tour="current-cash"]',
        position: 'bottom',
      },
      {
        id: 'forecast-ceo',
        title: `${getCashDurationShortTerm().charAt(0).toUpperCase() + getCashDurationShortTerm().slice(1)} Indicator`,
        content: `Shows months of ${getCashDurationShortTerm()} at current burn rate. This is your key metric for financial health.`,
        target: '[data-tour="forecast"]',
        position: 'bottom',
        roles: ['executive'],
      },
      {
        id: 'forecast-manager',
        title: `30-Day ${getForecastTerm(true)}`,
        content: 'See where your cash is heading. Colour zones indicate risk levels against your £50K threshold.',
        target: '[data-tour="forecast"]',
        position: 'bottom',
        roles: ['manager', 'cfo', 'regional'],
      },
      {
        id: 'forecast-preparer',
        title: `${getForecastTerm(true)} Status`,
        content: 'Green means above threshold (£50K), amber means approaching, red means below.',
        target: '[data-tour="forecast"]',
        position: 'bottom',
        roles: ['preparer', 'external'],
      },
      {
        id: 'variance',
        title: 'Variance Analysis',
        content: 'Compare your actual cash position against your forecast. Green means you\'re ahead, red means behind.',
        target: '[data-tour="variance"]',
        position: 'bottom',
        roles: ['manager', 'cfo', 'regional'],
      },
      {
        id: 'timeline-ceo',
        title: 'Quick Visual Check',
        content: 'Tap any point to see what\'s driving that day\'s position. Simple and fast.',
        target: '[data-tour="timeline-chart"]',
        position: 'top',
        roles: ['executive'],
      },
      {
        id: 'timeline-manager',
        title: 'Timeline Chart',
        content: 'Visualise your cash flow over time. Click any point to drill down to underlying transactions.',
        target: '[data-tour="timeline-chart"]',
        position: 'top',
        roles: ['manager', 'cfo', 'regional'],
      },
      {
        id: 'timeline-preparer',
        title: 'Timeline Chart',
        content: 'If numbers look wrong, check Transactions page for uncategorised items. Click any point to see which invoices/bills drive that day\'s total.',
        target: '[data-tour="timeline-chart"]',
        position: 'top',
        roles: ['preparer', 'external'],
      },
      {
        id: 'pending-actions',
        title: 'Pending Actions',
        content: 'Stay on top of important tasks like overdue invoices and upcoming payments.',
        target: '[data-tour="pending-actions"]',
        position: 'left',
      },
      {
        id: 'completion',
        title: 'Tour Complete!',
        content: 'You\'re all set! Need help with specific data? Click the 💬 chat button to ask questions.',
        position: 'bottom',
      },
    ],
  },
  {
    id: 'understanding-timeline',
    name: 'Understanding Timeline',
    description: 'Master the timeline chart and forecasting',
    duration: '8 min',
    roles: ['manager', 'cfo', 'regional'],
    steps: [
      {
        id: 'intro',
        title: 'Understanding the Timeline',
        content: 'The timeline chart shows 90-day projected cash with daily granularity. Let\'s explore how to read it.',
        position: 'bottom',
      },
      {
        id: 'line',
        title: 'The Cash Flow Line',
        content: 'This line shows your projected cash balance over time. Past data is solid, future projections are shown with confidence levels.',
        target: '[data-tour="timeline-chart"]',
        position: 'top',
      },
      {
        id: 'zones',
        title: 'Health Zones',
        content: 'Colour zones indicate risk levels against your £50K threshold. Green = healthy, amber = caution, red = critical.',
        target: '[data-tour="timeline-chart"]',
        position: 'top',
      },
      {
        id: 'threshold',
        title: 'Minimum Threshold',
        content: 'The red dashed line shows your minimum cash threshold (£50K). Try to stay above this line.',
        target: '[data-tour="timeline-chart"]',
        position: 'top',
      },
      {
        id: 'hover',
        title: 'Hover for Details',
        content: `Hover over any point on the chart to see exact amounts, ${getCashMovementTerms().in.toLowerCase()}/${getCashMovementTerms().out.toLowerCase()} transactions, and net change.`,
        target: '[data-tour="timeline-chart"]',
        position: 'top',
      },
      {
        id: 'click',
        title: 'Click to Drill Down',
        content: 'Click any point to see all transactions for that day. Variance panel below compares forecast vs actual to track accuracy.',
        target: '[data-tour="timeline-chart"]',
        position: 'top',
      },
      {
        id: 'date-range',
        title: 'Date Range Selector',
        content: 'Use date range selector to view 7/30/90 day windows for different planning horizons.',
        position: 'bottom',
      },
    ],
  },
  {
    id: 'managing-transactions',
    name: 'Managing Transactions',
    description: 'Categorise and adjust transaction data',
    duration: '6 min',
    roles: ['preparer', 'manager', 'external'],
    steps: [
      {
        id: 'intro-preparer',
        title: 'Managing Transactions',
        content: 'Learn how to categorise transactions and ensure accurate forecasts.',
        navigateTo: 'transactions',
        position: 'bottom',
        roles: ['preparer', 'external'],
      },
      {
        id: 'intro-manager',
        title: 'Transaction Management',
        content: 'Review and adjust transaction categorisation to improve forecast accuracy.',
        navigateTo: 'transactions',
        position: 'bottom',
        roles: ['manager', 'cfo', 'regional'],
      },
      {
        id: 'table',
        title: 'Transaction Table',
        content: 'All your transactions are listed here with date, description, amount, and category.',
        target: '[data-tour="transactions-table"]',
        position: 'top',
      },
      {
        id: 'filters',
        title: 'Filter Buttons',
        content: `Quickly filter by All, ${getCashMovementTerms().in}, ${getCashMovementTerms().out}, or Pending transactions.`,
        target: '[data-tour="transaction-filters"]',
        position: 'bottom',
      },
      {
        id: 'uncategorized-preparer',
        title: 'Finding Uncategorised Items',
        content: 'Check for uncategorised transactions regularly - they can affect forecast accuracy.',
        target: '[data-tour="transactions-table"]',
        position: 'top',
        roles: ['preparer', 'external'],
      },
      {
        id: 'edit',
        title: 'Edit Transactions',
        content: 'Click any transaction to edit its category, add notes, or adjust the forecast impact.',
        target: '[data-tour="transactions-table"]',
        position: 'top',
      },
    ],
  },
  {
    id: 'creating-scenarios',
    name: 'Creating Scenarios',
    description: 'Model different business outcomes',
    duration: '7 min',
    roles: ['manager', 'cfo', 'regional', 'executive'],
    steps: [
      {
        id: 'intro-ceo',
        title: 'Quick Scenario Modeling',
        content: 'Test decisions on the go. Model "what-if" situations in seconds.',
        navigateTo: 'scenarios',
        position: 'bottom',
        roles: ['executive'],
      },
      {
        id: 'intro-manager',
        title: 'Creating Scenarios',
        content: 'Scenarios let you model "what-if" situations. Let\'s create one together.',
        navigateTo: 'scenarios',
        position: 'bottom',
        roles: ['manager', 'cfo', 'regional'],
      },
      {
        id: 'new-button',
        title: 'New Scenario Button',
        content: 'Click here to create a new scenario. You can model delays, new contracts, or any business change.',
        target: '[data-tour="new-scenario"]',
        position: 'bottom',
      },
      {
        id: 'name',
        title: 'Name Your Scenario',
        content: 'Give your scenario a descriptive name like "BigCo Payment Delayed" or "New Contract Secured".',
        target: '[data-tour="scenario-name"]',
        position: 'bottom',
      },
      {
        id: 'adjust',
        title: 'Adjust Transactions',
        content: 'Modify transaction dates, amounts, or add new transactions to model your scenario.',
        target: '[data-tour="scenario-adjustments"]',
        position: 'left',
      },
      {
        id: 'preview',
        title: 'Impact Preview',
        content: 'See how your changes affect the cash flow forecast in real-time.',
        target: '[data-tour="scenario-preview"]',
        position: 'left',
      },
      {
        id: 'save',
        title: 'Save Scenario',
        content: 'Save your scenario to compare it with others or share with your team.',
        target: '[data-tour="save-scenario"]',
        position: 'bottom',
      },
    ],
  },
  {
    id: 'variance-analysis',
    name: 'Variance Analysis',
    description: 'Track forecast accuracy',
    duration: '9 min',
    roles: ['manager', 'cfo', 'regional'],
    steps: [
      {
        id: 'intro',
        title: 'Variance Analysis',
        content: 'Learn how to analyse the difference between your forecast and actual cash position to improve accuracy.',
        navigateTo: 'variance',
        position: 'bottom',
      },
      {
        id: 'overview',
        title: 'Variance Overview',
        content: 'See at a glance if you\'re ahead or behind your forecast, and by how much.',
        target: '[data-tour="variance-overview"]',
        position: 'bottom',
      },
      {
        id: 'breakdown',
        title: 'Variance Breakdown',
        content: 'Understand what\'s causing the variance - delayed payments, unexpected expenses, etc.',
        target: '[data-tour="variance-breakdown"]',
        position: 'top',
      },
      {
        id: 'trends',
        title: 'Variance Trends',
        content: 'Track how your forecast accuracy improves over time.',
        target: '[data-tour="variance-trends"]',
        position: 'top',
      },
      {
        id: 'actions',
        title: 'Recommended Actions',
        content: 'Get suggestions on how to improve forecast accuracy or address variances.',
        target: '[data-tour="variance-actions"]',
        position: 'top',
      },
    ],
  },
  {
    id: 'ai-recommendations',
    name: 'AI Recommendations',
    description: 'Get actionable cash insights',
    duration: '12 min',
    roles: ['manager', 'cfo', 'regional'],
    steps: [
      {
        id: 'intro',
        title: 'AI-Powered Insights',
        content: 'Access CFF analyses your cash flow patterns and provides actionable recommendations.',
        position: 'bottom',
      },
      {
        id: 'recommendations',
        title: 'Smart Recommendations',
        content: 'Get AI-powered suggestions for improving cash flow, reducing risk, and optimising working capital.',
        position: 'bottom',
      },
      {
        id: 'patterns',
        title: 'Pattern Detection',
        content: 'The AI identifies recurring patterns, seasonal trends, and anomalies in your cash flow.',
        position: 'bottom',
      },
      {
        id: 'actions',
        title: 'Actionable Steps',
        content: 'Each recommendation includes specific actions you can take to improve your cash position.',
        position: 'bottom',
      },
    ],
  },
  {
    id: 'data-assistant',
    name: 'Using Data Assistant',
    description: 'Ask questions in plain English',
    duration: '5 min',
    steps: [
      {
        id: 'intro',
        title: 'Your Data Assistant',
        content: 'The Data Assistant lets you ask questions about your cash flow in plain English.',
        position: 'bottom',
      },
      {
        id: 'examples',
        title: 'Example Questions',
        content: 'Try asking: "Where does £247K come from?", "Show me overdue invoices", or "What\'s my cash position next week?"',
        position: 'bottom',
      },
      {
        id: 'actions',
        title: 'Quick Actions',
        content: 'The assistant can highlight data, navigate to pages, and even start tours to help you learn.',
        position: 'bottom',
      },
    ],
  },
  {
    id: 'approval-workflows',
    name: 'Approval Workflows',
    description: 'Review and approve forecasts',
    duration: '6 min',
    roles: ['manager', 'cfo', 'regional'],
    steps: [
      {
        id: 'intro',
        title: 'Approval Workflows',
        content: 'Learn how to review and approve forecasts prepared by your team.',
        position: 'bottom',
      },
      {
        id: 'pending',
        title: 'Pending Approvals',
        content: 'See all forecasts waiting for your review and approval.',
        position: 'bottom',
      },
      {
        id: 'review',
        title: 'Review Process',
        content: 'Check forecast accuracy, review assumptions, and add comments before approving.',
        position: 'bottom',
      },
    ],
  },
  {
    id: 'ceo-10-second-check',
    name: 'Your 10-Second Check',
    description: 'Mobile dashboard for executives',
    duration: '3 min',
    roles: ['executive'],
    steps: [
      {
        id: 'intro',
        title: 'Your 10-Second Check',
        content: 'Get the critical information you need in 10 seconds or less.',
        position: 'bottom',
      },
      {
        id: 'status',
        title: 'Health Status',
        content: 'Green = healthy, amber = attention needed, red = action required. That\'s all you need to know.',
        target: '[data-tour="timeline-chart"]',
        position: 'top',
      },
      {
        id: 'runway',
        title: `${getCashDurationShortTerm().charAt(0).toUpperCase() + getCashDurationShortTerm().slice(1)} Indicator`,
        content: `See how many months of cash ${getCashDurationShortTerm()} you have at current burn rate.`,
        target: '[data-tour="forecast"]',
        position: 'bottom',
      },
    ],
  },
  {
    id: 'transaction-management-preparer',
    name: 'Transaction Management',
    description: 'Categorisation deep dive',
    duration: '10 min',
    roles: ['preparer', 'external'],
    steps: [
      {
        id: 'intro',
        title: 'Transaction Management',
        content: 'Master transaction categorisation to ensure accurate forecasts.',
        navigateTo: 'transactions',
        position: 'bottom',
      },
      {
        id: 'categorization',
        title: 'Categorisation Rules',
        content: 'Learn the rules for categorising different types of transactions.',
        position: 'bottom',
      },
      {
        id: 'bulk-edit',
        title: 'Bulk Editing',
        content: 'Select multiple transactions to categorise them all at once.',
        position: 'bottom',
      },
    ],
  },
  {
    id: 'troubleshooting-forecasts',
    name: 'Troubleshooting Forecasts',
    description: 'When numbers don\'t match',
    duration: '8 min',
    roles: ['preparer', 'external'],
    steps: [
      {
        id: 'intro',
        title: 'Troubleshooting Forecasts',
        content: 'Learn how to identify and fix common forecast issues.',
        position: 'bottom',
      },
      {
        id: 'uncategorized',
        title: 'Check Uncategorised',
        content: 'Uncategorised transactions are the #1 cause of forecast errors.',
        position: 'bottom',
      },
      {
        id: 'duplicates',
        title: 'Find Duplicates',
        content: 'Look for duplicate transactions that might be inflating your forecast.',
        position: 'bottom',
      },
    ],
  },
  {
    id: 'managing-data-sources',
    name: 'Managing Data Sources',
    description: 'Connect and configure integrations',
    duration: '7 min',
    roles: ['system_admin'],
    steps: [
      {
        id: 'intro',
        title: 'Managing Data Sources',
        content: 'Learn how to connect new data sources, configure sync frequencies, and troubleshoot connection issues.',
        navigateTo: 'settings',
        position: 'bottom',
      },
      {
        id: 'connecting',
        title: 'Connecting New Sources',
        content: 'Navigate to Settings → Data Sources to add new integrations like Access Financials, Paycircle, or CRM systems.',
        position: 'bottom',
      },
      {
        id: 'testing',
        title: 'Testing Connections',
        content: 'After connecting, use the "Test Connection" button to verify credentials and data access before enabling sync.',
        position: 'bottom',
      },
      {
        id: 'sync-frequency',
        title: 'Configuring Sync Frequencies',
        content: 'Set sync schedules: Real-time (database triggers), Hourly, Nightly, or Daily. Real-time is recommended for financial data, nightly for payroll.',
        position: 'bottom',
      },
      {
        id: 'troubleshooting',
        title: 'Troubleshooting Sync Failures',
        content: 'Check sync status in Data Sources. Common issues: expired credentials, API rate limits, or network connectivity. View detailed logs for diagnostics.',
        position: 'bottom',
      },
      {
        id: 'monitoring',
        title: 'Monitoring Sync Health',
        content: 'The dashboard shows last sync time and status for each source. Set up alerts for sync failures to catch issues early.',
        position: 'bottom',
      },
    ],
  },
  {
    id: 'configuring-terminology',
    name: 'Configuring Terminology',
    description: 'Customise company-wide labels',
    duration: '5 min',
    roles: ['system_admin'],
    steps: [
      {
        id: 'intro',
        title: 'Configuring Terminology',
        content: 'Customise labels and terminology to match your company\'s language. This affects all users system-wide.',
        navigateTo: 'settings',
        position: 'bottom',
      },
      {
        id: 'why-matters',
        title: 'Why Terminology Matters',
        content: `Different industries use different terms. Construction companies might say "Coverage" instead of "${getCashDurationShortTerm().charAt(0).toUpperCase() + getCashDurationShortTerm().slice(1)}", or "Retention" instead of "Receivables".`,
        position: 'bottom',
      },
      {
        id: 'labels',
        title: 'Custom Labels',
        content: `Change labels like "Invoice" to "Sales Order", "Bill" to "Purchase Order", or "${getCashDurationShortTerm().charAt(0).toUpperCase() + getCashDurationShortTerm().slice(1)}" to "Coverage". Changes apply immediately.`,
        position: 'bottom',
      },
      {
        id: 'preview',
        title: 'Live Preview',
        content: 'See a live preview of how terminology changes will appear throughout the system before saving.',
        position: 'bottom',
      },
      {
        id: 'system-wide',
        title: 'System-Wide Impact',
        content: 'Terminology changes affect all dashboards, reports, and user interfaces. All users will see the new labels immediately after you save.',
        position: 'bottom',
      },
    ],
  },
  {
    id: 'managing-user-roles',
    name: 'User & Role Management',
    description: 'Add users and assign permissions',
    duration: '8 min',
    roles: ['system_admin'],
    steps: [
      {
        id: 'intro',
        title: 'User & Role Management',
        content: 'Learn how to add new users, assign roles, and configure permissions to control who can view, edit, and approve data.',
        navigateTo: 'settings',
        position: 'bottom',
      },
      {
        id: 'adding-users',
        title: 'Adding New Users',
        content: 'Navigate to Settings → Users → Add User. Enter email, name, and select their role. They\'ll receive an invitation email to set up their account.',
        position: 'bottom',
      },
      {
        id: 'roles',
        title: 'Role Types',
        content: 'Available roles: Preparer (data entry), Manager (review & approve), CFO (full financial access), Executive (high-level view), Admin (system configuration).',
        position: 'bottom',
      },
      {
        id: 'permissions',
        title: 'Permission Levels',
        content: 'Preparers can edit transactions. Managers can approve forecasts. CFOs see all financial data. Executives get simplified dashboards. Admins configure everything.',
        position: 'bottom',
      },
      {
        id: 'assigning-roles',
        title: 'Assigning Roles',
        content: 'You can change a user\'s role at any time. Click the user → Edit → Select new role. Changes take effect immediately.',
        position: 'bottom',
      },
      {
        id: 'access-levels',
        title: 'Managing Access Levels',
        content: 'Control access to specific data sources, scenarios, or reports. Use "Access Control" to restrict sensitive financial data to specific roles.',
        position: 'bottom',
      },
      {
        id: 'deactivating',
        title: 'Deactivating Users',
        content: 'When someone leaves, deactivate their account instead of deleting it. This preserves audit trails while revoking access immediately.',
        position: 'bottom',
      },
    ],
  },
  {
    id: 'system-monitoring',
    name: 'System Monitoring',
    description: 'Track activity and system health',
    duration: '6 min',
    roles: ['system_admin'],
    steps: [
      {
        id: 'intro',
        title: 'System Monitoring',
        content: 'Learn how to monitor system health, view activity logs, check sync status, and export audit reports.',
        navigateTo: 'settings',
        position: 'bottom',
      },
      {
        id: 'activity-logs',
        title: 'Viewing Activity Logs',
        content: 'Settings → Activity Logs shows all user actions: logins, data changes, approvals, and configuration updates. Filter by user, date, or action type.',
        position: 'bottom',
      },
      {
        id: 'system-health',
        title: 'Checking System Health',
        content: 'The System Health dashboard shows: API response times, database performance, sync queue status, and error rates. Green = healthy, amber = degraded, red = critical.',
        position: 'bottom',
      },
      {
        id: 'sync-status',
        title: 'Understanding Sync Status',
        content: 'Monitor all data source sync jobs: last sync time, next scheduled sync, success/failure status, and error messages. Set up email alerts for failures.',
        position: 'bottom',
      },
      {
        id: 'audit-reports',
        title: 'Exporting Audit Reports',
        content: 'Generate compliance reports showing all data changes, user access, and system events. Export as CSV or PDF for external auditors.',
        position: 'bottom',
      },
      {
        id: 'alerts',
        title: 'Configuring Alerts',
        content: 'Set up email or SMS alerts for: sync failures, system errors, unusual activity, or performance degradation. Stay informed without constant monitoring.',
        position: 'bottom',
      },
    ],
  },
  {
    id: 'quick-scenario-modeling',
    name: 'Quick Scenario Modeling',
    description: 'Test decisions on the go',
    duration: '5 min',
    roles: ['executive'],
    steps: [
      {
        id: 'intro',
        title: 'Quick Scenario Modeling',
        content: 'Model "what-if" situations in seconds to test business decisions.',
        navigateTo: 'scenarios',
        position: 'bottom',
      },
      {
        id: 'simple',
        title: 'Keep It Simple',
        content: 'Focus on the big decisions: delay a payment, land a new contract, or adjust timing.',
        position: 'bottom',
      },
      {
        id: 'impact',
        title: 'See Impact Instantly',
        content: `Watch how your decision affects cash ${getCashDurationShortTerm()} in real-time.`,
        position: 'bottom',
      },
    ],
  },
];

export const TourProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentRoleId, setCurrentRoleId] = useState<string>('system_admin');
  const [activeTour, setActiveTour] = useState<Tour | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // Load tour state from localStorage
  const [tourState, setTourState] = useState<TourState>(() => {
    const stored = localStorage.getItem('cff-tour-state');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return {
          userId: 'default',
          completedTours: [],
          inProgressTours: {},
          hideWelcome: false,
        };
      }
    }
    return {
      userId: 'default',
      completedTours: [],
      inProgressTours: {},
      hideWelcome: false,
    };
  });

  // Save tour state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cff-tour-state', JSON.stringify(tourState));
  }, [tourState]);

  // Filter tours by current role ID
  const tours = ALL_TOURS.filter(tour => {
    if (!tour.roles || tour.roles.length === 0) return true;
    return tour.roles.includes(currentRoleId);
  });

  // Filter steps by current role ID
  const getFilteredSteps = (tour: Tour): TourStep[] => {
    return tour.steps.filter(step => {
      if (!step.roles || step.roles.length === 0) return true;
      return step.roles.includes(currentRoleId);
    });
  };

  const hasSeenWelcomePrompt = tourState.hideWelcome;

  const setHasSeenWelcomePrompt = (seen: boolean) => {
    setTourState(prev => ({
      ...prev,
      hideWelcome: seen,
    }));
  };

  const isTourCompleted = (tourId: string): boolean => {
    return tourState.completedTours.includes(tourId);
  };

  const startTour = (tourId: string) => {
    const tour = ALL_TOURS.find((t) => t.id === tourId);
    if (tour) {
      const filteredTour = {
        ...tour,
        steps: getFilteredSteps(tour),
      };
      setActiveTour(filteredTour);
      setCurrentStepIndex(0);
      setIsMenuOpen(false);
    }
  };

  const nextStep = () => {
    if (activeTour && currentStepIndex < activeTour.steps.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    } else {
      completeTour();
    }
  };

  const previousStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
    }
  };

  const skipTour = () => {
    setActiveTour(null);
    setCurrentStepIndex(0);
  };

  const completeTour = () => {
    if (activeTour) {
      setTourState(prev => ({
        ...prev,
        completedTours: [...new Set([...prev.completedTours, activeTour.id])],
        inProgressTours: Object.fromEntries(
          Object.entries(prev.inProgressTours).filter(([id]) => id !== activeTour.id)
        ),
      }));
      setHasSeenWelcomePrompt(true);
    }
    setActiveTour(null);
    setCurrentStepIndex(0);
  };

  const replayTour = (tourId: string) => {
    startTour(tourId);
  };

  const getTourState = () => tourState;

  return (
    <TourContext.Provider
      value={{
        tours,
        activeTour,
        currentStepIndex,
        isMenuOpen,
        setIsMenuOpen,
        startTour,
        nextStep,
        previousStep,
        skipTour,
        completeTour,
        replayTour,
        hasSeenWelcomePrompt,
        setHasSeenWelcomePrompt,
        isTourCompleted,
        getTourState,
        currentRoleId,
        setCurrentRoleId,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};

export const useTour = () => {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};
